import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Crown, CheckCircle2, Sparkles, Users, Copy, Clock, Building2, ShieldCheck, Mail } from "lucide-react";

const PLAN_FEATURES = {
  free: ["5 CV sends", "Smart match ranking", "Platform inbox", "Basic tracking"],
  basic: [
    "100 sends / month",
    "Tailored cover letters",
    "CV review unlimited",
    "Interview coach",
    "Advanced tracking",
  ],
  pro: [
    "Unlimited sends",
    "Priority queue",
    "Deal Closer (negotiation + brief)",
    "Career GPS",
    "White-glove CV rewrite",
    "Private recruiter intros",
  ],
  agency: [
    "Every feature — unlocked",
    "Multi-seat agency dashboard",
    "Full Recruiter Portal access",
    "Bulk candidate outreach",
    "API / whitelabel option",
    "Dedicated IT support add-on",
  ],
};

const PLAN_ICON = {
  free: Clock,
  basic: Sparkles,
  pro: Crown,
  agency: Building2,
};

function EmailTestCard() {
  const [sending, setSending] = React.useState(false);
  const [result, setResult] = React.useState(null);

  const send = async () => {
    setSending(true);
    setResult(null);
    try {
      const r = await api.post("/email/test");
      setResult({ ok: true, to: r.data.to });
      toast.success(`Test email sent to ${r.data.to}`);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Failed";
      setResult({ ok: false, msg });
      toast.error(`Email test failed: ${msg}`);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="border border-black/10 bg-white p-6" data-testid="email-test-card">
      <div className="flex items-start gap-3 mb-3">
        <Mail className="w-5 h-5 text-[#0033FF] mt-0.5" />
        <div>
          <div className="font-display font-bold text-lg">Email system health check</div>
          <p className="text-xs text-gray-600 mt-1">
            Send a real test email to yourself to confirm Resend is wired correctly. Check your inbox
            within 30 seconds after clicking.
          </p>
        </div>
      </div>
      <button
        onClick={send}
        disabled={sending}
        className="mt-2 bg-black text-white hover:bg-[#0033FF] font-bold px-5 py-3 text-sm flex items-center gap-2 transition-colors disabled:opacity-60"
        data-testid="send-test-email"
      >
        {sending ? "Sending…" : "Send test email to me"}
      </button>
      {result?.ok && (
        <div className="mt-3 text-sm text-[#34C759] font-mono">
          ✓ Sent to {result.to} — check your inbox (and spam folder).
        </div>
      )}
      {result && !result.ok && (
        <div className="mt-3 text-sm text-[#FF3B30] font-mono">✗ {result.msg}</div>
      )}
    </div>
  );
}

export default function Billing() {
  const { t } = useI18n();
  const { user, setUser } = useAuth();
  const [plans, setPlans] = useState([]);
  const [usage, setUsage] = useState(null);
  const [ref, setRef] = useState(null);
  const [code, setCode] = useState("");
  const [upgrading, setUpgrading] = useState(null);
  const [gateway, setGateway] = useState(() => localStorage.getItem("jf_gateway") || "stripe");
  const [statusNote, setStatusNote] = useState(null);

  // Persist gateway choice
  useEffect(() => { localStorage.setItem("jf_gateway", gateway); }, [gateway]);

  // Poll payment status if we just came back from a checkout
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sid = params.get("session_id");
    const gw = params.get("gateway") || "stripe";
    const cancelled = params.get("cancelled");
    if (cancelled) {
      toast.error("Payment cancelled");
      window.history.replaceState({}, "", window.location.pathname);
      return;
    }
    if (!sid) return;
    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      try {
        const r = await api.get(`/billing/checkout/status/${sid}`, { params: { gateway: gw } });
        const st = r.data.payment_status;
        if (r.data.processed && st === "paid") {
          toast.success(`Payment confirmed — welcome to ${r.data.plan.toUpperCase()}`);
          const me = await api.get("/auth/me");
          setUser(me.data);
          const u = await api.get("/usage");
          setUsage(u.data);
          window.history.replaceState({}, "", window.location.pathname);
          setStatusNote(null);
          return;
        }
        if (st === "expired" || st === "failed" || st === "canceled") {
          toast.error(`Payment ${st}`);
          window.history.replaceState({}, "", window.location.pathname);
          return;
        }
        if (attempts < 10) {
          setStatusNote("Confirming your payment…");
          setTimeout(poll, 2000);
        } else {
          setStatusNote("Still verifying — refresh in a minute.");
        }
      } catch {
        if (attempts < 10) setTimeout(poll, 2500);
      }
    };
    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    api.get("/billing/plans").then((r) => setPlans(r.data.plans || [])).catch(() => {});
    api.get("/usage").then((r) => setUsage(r.data)).catch(() => {});
    api.get("/referral/me").then((r) => setRef(r.data)).catch(() => {});
  }, []);

  const upgrade = async (plan) => {
    // "free" is auto-assigned, not payable.
    if (plan === "free") return;
    setUpgrading(plan);
    try {
      const r = await api.post("/billing/checkout", {
        plan,
        gateway,
        origin_url: window.location.origin,
      });
      if (r.data?.url) {
        window.location.href = r.data.url;
        return;
      }
      toast.error("Checkout unavailable");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Checkout failed";
      toast.error(typeof msg === "string" ? msg : "Checkout failed");
    } finally {
      setUpgrading(null);
    }
  };

  const redeem = async () => {
    if (!code.trim()) return;
    try {
      const r = await api.post("/referral/redeem", { code });
      toast.success(`+${r.data.bonus_sends_added} bonus sends added`);
      setCode("");
      const me = await api.get("/referral/me");
      setRef(me.data);
      const u = await api.get("/usage");
      setUsage(u.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid code");
    }
  };

  const copy = () => {
    if (ref?.code) {
      navigator.clipboard.writeText(ref.code);
      toast.success("Code copied");
    }
  };

  const currentPlan = user?.subscription || "free";
  const trialLeft = usage?.trial_days_left;
  const trialExpired = !!usage?.trial_expired;

  return (
    <div className="space-y-10 max-w-6xl" data-testid="billing-page">
      <div>
        <div className="font-mono text-xs uppercase tracking-[0.2em] text-gray-500 mb-3">/ {t("billing")}</div>
        <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter">Plan & Credits</h1>
      </div>

      {/* Trial banner */}
      {currentPlan === "free" && usage && (
        <div
          className={`border p-5 flex items-center gap-4 ${
            trialExpired ? "border-[#FF3B30] bg-[#FF3B30]/5" : "border-black/10 bg-[#F8F9FA]"
          }`}
          data-testid="trial-banner"
        >
          <Clock className={`w-6 h-6 ${trialExpired ? "text-[#FF3B30]" : "text-[#0033FF]"}`} />
          <div className="flex-1">
            <div className="font-display font-bold">
              {trialExpired
                ? "Your 7-day free trial has ended"
                : `${trialLeft} day${trialLeft === 1 ? "" : "s"} left on your free trial`}
            </div>
            <div className="text-xs text-gray-600">
              {trialExpired
                ? "Pick a plan below to resume applying. Your data, applications and CV are kept."
                : "Upgrade anytime — your 5 free sends carry over."}
            </div>
          </div>
        </div>
      )}

      {usage && (
        <div className="border border-black/10 bg-[#F8F9FA] p-6 md:p-8 flex flex-wrap gap-8 items-center">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500 mb-1">Current plan</div>
            <div className="font-display font-black text-3xl uppercase">
              <Crown className="w-6 h-6 inline me-2 text-[#FF3B30]" />
              {usage.plan_name || usage.plan}
            </div>
          </div>
          <div className="flex-1 min-w-[200px]">
            <div className="flex justify-between font-mono text-xs mb-2">
              <span>Sends used</span>
              <span>
                <span className="num-ticker font-bold">{usage.used}</span> / {usage.total_limit}
              </span>
            </div>
            <div className="w-full h-3 bg-white border border-black/10">
              <div
                className="h-full bg-[#0033FF]"
                style={{
                  width: `${Math.min(100, (usage.used / Math.max(usage.total_limit, 1)) * 100)}%`,
                }}
              />
            </div>
            {usage.bonus_sends > 0 && (
              <div className="mt-2 text-xs text-[#34C759] font-mono">
                +{usage.bonus_sends} bonus sends from referrals
              </div>
            )}
          </div>
        </div>
      )}

      {/* Gateway selector */}
      <div className="border border-black/10 bg-white p-5 flex items-center justify-between gap-4 flex-wrap" data-testid="gateway-card">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500">Payment method</div>
          <div className="font-display font-bold text-lg">
            {gateway === "stripe" ? "Stripe · cards (international)" : "Moyasar · mada · STC Pay · Apple Pay"}
          </div>
          {statusNote && (
            <div className="mt-1 font-mono text-xs text-[#0033FF]">{statusNote}</div>
          )}
        </div>
        <div className="flex gap-0 border border-black/10 bg-[#F8F9FA]">
          <button
            onClick={() => setGateway("stripe")}
            className={`px-4 py-2 text-sm font-bold transition-colors ${
              gateway === "stripe" ? "bg-black text-white" : "hover:bg-black/5"
            }`}
            data-testid="gateway-stripe"
          >
            Stripe
          </button>
          <button
            onClick={() => setGateway("moyasar")}
            className={`px-4 py-2 text-sm font-bold transition-colors ${
              gateway === "moyasar" ? "bg-[#0033FF] text-white" : "hover:bg-black/5"
            }`}
            data-testid="gateway-moyasar"
          >
            Moyasar
          </button>
        </div>
      </div>

      {/* Plans grid — 4 columns */}
      <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-6" data-testid="plans">
        {plans.map((p) => {
          const active = currentPlan === p.id;
          const isHot = p.id === "basic";
          const isAgency = p.id === "agency";
          const Icon = PLAN_ICON[p.id] || Sparkles;
          const features = PLAN_FEATURES[p.id] || [];
          const priceLabel =
            p.id === "free"
              ? `${p.display}`
              : `${p.display}`;
          const periodLabel =
            p.period === "trial"
              ? "· 7-day trial"
              : p.period === "year"
              ? "/ year"
              : "/ month";
          return (
            <div
              key={p.id}
              className={`p-8 border relative transition-all hover:translate-y-[-4px] flex flex-col ${
                isAgency
                  ? "bg-[#0033FF] text-white border-[#0033FF]"
                  : isHot
                  ? "bg-black text-white border-black"
                  : "bg-white border-black/10"
              }`}
              data-testid={`plan-${p.id}`}
            >
              {isHot && (
                <div className="absolute -top-3 start-6 bg-[#FF3B30] text-white text-xs font-bold px-3 py-1">
                  MOST POPULAR
                </div>
              )}
              {isAgency && (
                <div className="absolute -top-3 start-6 bg-[#34C759] text-white text-xs font-bold px-3 py-1">
                  FOR AGENCIES
                </div>
              )}
              <div className="flex items-center gap-2 mb-4">
                <Icon
                  className={`w-4 h-4 ${
                    isAgency ? "text-white" : isHot ? "text-[#FF3B30]" : "text-[#0033FF]"
                  }`}
                />
                <span
                  className={`font-mono text-xs uppercase tracking-widest ${
                    isAgency ? "text-white/80" : isHot ? "text-[#FF3B30]" : "text-gray-500"
                  }`}
                >
                  {p.name}
                </span>
              </div>
              <div className="mb-6">
                <div className="flex items-baseline gap-2">
                  <span className="font-display font-black text-4xl tracking-tighter">{priceLabel}</span>
                </div>
                <div
                  className={`font-mono text-xs mt-1 ${
                    isAgency || isHot ? "text-white/70" : "text-gray-500"
                  }`}
                >
                  {periodLabel}
                </div>
                {isAgency && (
                  <div className="mt-3 text-xs bg-white/10 border border-white/20 p-3">
                    <div className="flex justify-between">
                      <span>License</span>
                      <span className="font-bold">SAR 30,000 / yr</span>
                    </div>
                    <div className="flex justify-between">
                      <span>IT support</span>
                      <span className="font-bold">SAR 3,000 / yr</span>
                    </div>
                    <div className="border-t border-white/20 mt-2 pt-2 flex justify-between font-bold">
                      <span>Total</span>
                      <span>SAR 33,000 / yr</span>
                    </div>
                  </div>
                )}
              </div>
              <ul className="space-y-2 mb-8 text-sm flex-1">
                {features.map((f) => (
                  <li key={f} className="flex items-start gap-2">
                    <CheckCircle2
                      className={`w-4 h-4 flex-shrink-0 mt-0.5 ${
                        isAgency ? "text-white" : isHot ? "text-[#34C759]" : "text-[#0033FF]"
                      }`}
                    />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <button
                onClick={() => upgrade(p.id)}
                disabled={active || upgrading === p.id || p.id === "free"}
                className={`w-full py-3 font-bold transition-colors ${
                  active || p.id === "free"
                    ? "bg-gray-200 text-gray-500 cursor-default"
                    : isAgency
                    ? "bg-white text-[#0033FF] hover:bg-black hover:text-white"
                    : isHot
                    ? "bg-white text-black hover:bg-[#FF3B30] hover:text-white"
                    : "bg-black text-white hover:bg-[#0033FF]"
                }`}
                data-testid={`upgrade-${p.id}`}
              >
                {active
                  ? "Current plan"
                  : upgrading === p.id
                  ? "…"
                  : p.id === "free"
                  ? "Automatic on signup"
                  : `Pay ${p.display}`}
              </button>
            </div>
          );
        })}
      </div>

      <div
        className="border border-black/10 bg-[#F8F9FA] p-5 flex items-start gap-3 text-xs text-gray-600"
        data-testid="agency-disclaimer"
      >
        <ShieldCheck className="w-4 h-4 text-[#0033FF] mt-0.5 shrink-0" />
        <span>
          <span className="font-bold text-black">Agency use requires the full license.</span> Individual
          tiers (Free, Basic, Pro) are for a single jobseeker — using Basic or Pro to run applications on
          behalf of clients is not permitted.
        </span>
      </div>

      <EmailTestCard />

      <section className="border border-black/10 bg-white p-8 space-y-5" data-testid="referral-section">
        <div className="flex items-center gap-2">
          <Users className="w-5 h-5" />
          <h2 className="font-display font-bold text-2xl">Referral Program</h2>
        </div>
        <p className="text-gray-600 text-sm">
          Invite friends. They get <span className="font-bold text-black">$5 in bonus CV sends</span>. You
          get <span className="font-bold text-[#0033FF]">$10 credit</span> auto-deducted from your next
          monthly bill.{" "}
          <Link to="/become-one-of-us" className="link-underline font-bold text-[#0033FF]">
            Learn more →
          </Link>
        </p>
        {ref && (
          <div className="grid md:grid-cols-4 gap-4">
            <div className="border border-black/10 p-5">
              <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500 mb-2">Your code</div>
              <div className="flex items-center gap-2">
                <span className="font-display font-black text-2xl tracking-tighter">{ref.code}</span>
                <button onClick={copy} className="p-2 border border-black/20 hover:bg-black hover:text-white">
                  <Copy className="w-4 h-4" />
                </button>
              </div>
            </div>
            <div className="border border-black/10 p-5">
              <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500 mb-2">Friends invited</div>
              <div className="num-ticker font-display font-black text-3xl">{ref.invited_count}</div>
            </div>
            <div className="border border-black/10 p-5">
              <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500 mb-2">Bonus sends earned</div>
              <div className="num-ticker font-display font-black text-3xl text-[#34C759]">+{ref.bonus_sends}</div>
            </div>
            <div className="border border-black p-5 bg-black text-white">
              <div className="font-mono text-[10px] uppercase tracking-widest text-gray-400 mb-2">Credit balance</div>
              <div className="num-ticker font-display font-black text-3xl text-[#34C759]">${ref.credit_usd || 0}</div>
              <div className="text-[10px] text-gray-400 mt-1 uppercase tracking-widest">next invoice</div>
            </div>
          </div>
        )}
        <div className="grid md:grid-cols-[1fr_auto] gap-3 items-end pt-4 border-t border-black/10">
          <label>
            <span className="block font-mono text-[10px] uppercase tracking-widest text-gray-500 mb-2">
              Have a friend's code?
            </span>
            <input
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder="ABCD1234"
              className="w-full bg-transparent border-b-2 border-gray-200 focus:border-black outline-none py-3 font-mono"
              data-testid="referral-input"
            />
          </label>
          <button
            onClick={redeem}
            className="bg-black text-white hover:bg-[#0033FF] font-bold px-6 py-3 flex items-center gap-2 transition-colors"
            data-testid="referral-redeem"
          >
            <Sparkles className="w-4 h-4" /> Redeem
          </button>
        </div>
      </section>
    </div>
  );
}
